import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Search, TrendingUp, Trash2 } from 'lucide-react';

interface Snippet {
  id: string;
  source: 'agent' | 'lorena';
  category: 'question' | 'objection' | 'phrase' | 'red_flag' | 'green_flag' | 'closing';
  content: string;
  context: string | null;
  times_seen: number;
  conversions_count: number;
  effectiveness: number;
  usage: 'always' | 'sometimes' | 'never';
  origin_kind?: 'live' | 'historical_approved' | 'historical_rejected';
}

const CATEGORY_LABELS: Record<Snippet['category'], string> = {
  question: 'Perguntas',
  objection: 'Objecoes',
  phrase: 'Frases',
  red_flag: 'Sinal de alerta',
  green_flag: 'Sinal positivo',
  closing: 'Fechamento',
};

export default function MirrorTab({ sellerId }: { sellerId: string }) {
  const [snippets, setSnippets] = useState<Snippet[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<Snippet['category'] | 'all'>('all');
  const [sourceFilter, setSourceFilter] = useState<Snippet['source'] | 'all'>('all');
  const [originFilter, setOriginFilter] = useState<'all' | 'live' | 'historical_approved' | 'historical_rejected'>('all');

  useEffect(() => {
    void load();
  }, [sellerId]);

  async function load() {
    setLoading(true);
    const { data } = await supabase
      .from('ai_learning_snippets')
      .select('*')
      .eq('seller_id', sellerId)
      .order('effectiveness', { ascending: false })
      .limit(200);
    setSnippets((data ?? []) as Snippet[]);
    setLoading(false);
  }

  async function updateUsage(id: string, usage: Snippet['usage']) {
    await supabase.from('ai_learning_snippets').update({ usage }).eq('id', id);
    void load();
  }

  async function remove(id: string) {
    if (!confirm('Remover esta linha do espelho?')) return;
    await supabase.from('ai_learning_snippets').delete().eq('id', id);
    void load();
  }

  if (loading) return <div className="text-sm text-slate-500">Carregando...</div>;

  const filtered = snippets.filter((s) => {
    if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
    if (sourceFilter !== 'all' && s.source !== sourceFilter) return false;
    if (originFilter !== 'all' && (s.origin_kind || 'live') !== originFilter) return false;
    if (search && !s.content.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-1 text-sm font-semibold text-slate-900">O que o agente aprendeu</div>
        <p className="text-xs text-slate-500">
          Cada linha vem da observacao das conversas: frases que convertem, perguntas boas, objecoes recorrentes.
          Voce decide se o agente deve usar sempre, as vezes ou nunca.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar..."
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value as any)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Todas categorias</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value as any)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Todas origens</option>
          <option value="lorena">Lorena</option>
          <option value="agent">Agente</option>
        </select>
        <select
          value={originFilter}
          onChange={(e) => setOriginFilter(e.target.value as any)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"
        >
          <option value="all">Todo aprendizado</option>
          <option value="live">Ao vivo</option>
          <option value="historical_approved">Historico aprovados</option>
          <option value="historical_rejected">Historico recusados</option>
        </select>
      </div>

      {filtered.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
          Nenhuma linha aprendida ainda.
        </div>
      )}

      <div className="space-y-2">
        {filtered.map((s) => (
          <div key={s.id} className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                {CATEGORY_LABELS[s.category]}
              </span>
              <span
                className={
                  'rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                  (s.source === 'lorena' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700')
                }
              >
                {s.source === 'lorena' ? 'Aprendido da Lorena' : 'Do agente'}
              </span>
              {s.origin_kind && s.origin_kind !== 'live' && (
                <span
                  className={
                    'rounded-full px-2 py-0.5 text-[10px] font-medium ' +
                    (s.origin_kind === 'historical_approved'
                      ? 'bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200'
                      : 'bg-red-50 text-red-800 ring-1 ring-red-200')
                  }
                >
                  {s.origin_kind === 'historical_approved' ? 'Historico aprovados' : 'Historico recusados'}
                </span>
              )}
              <span className="ml-auto inline-flex items-center gap-1 text-xs text-slate-500">
                <TrendingUp className="h-3 w-3" /> Efetividade {s.effectiveness.toFixed(0)}%
              </span>
            </div>
            <p className="text-sm text-slate-800">"{s.content}"</p>
            {s.context && <p className="mt-1 text-xs italic text-slate-500">Contexto: {s.context}</p>}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="text-slate-500">
                Visto {s.times_seen}x · {s.conversions_count} conversoes
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg bg-slate-100 p-0.5">
                  {(['always', 'sometimes', 'never'] as const).map((u) => (
                    <button
                      key={u}
                      onClick={() => updateUsage(s.id, u)}
                      className={
                        'rounded-md px-2 py-1 text-[11px] font-medium transition ' +
                        (s.usage === u
                          ? u === 'always'
                            ? 'bg-emerald-600 text-white'
                            : u === 'never'
                            ? 'bg-slate-900 text-white'
                            : 'bg-white text-slate-900 shadow-sm'
                          : 'text-slate-600 hover:text-slate-900')
                      }
                    >
                      {u === 'always' ? 'Sempre' : u === 'sometimes' ? 'As vezes' : 'Nunca'}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => remove(s.id)}
                  className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-red-200 hover:text-red-500"
                  aria-label="Remover"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}